"use client";

import { useMemo, useRef } from "react";
import {
  computeBatteryBankCurrents,
  type BatteryBankCurrents,
} from "@/lib/battery-bank";
import { type SimulateContext, type CableSpec } from "@/lib/cable-current";
import { useCanvasDrag } from "@/lib/canvas-drag";
import { getDefinition } from "@/lib/catalog";
import {
  computeCrankingCables,
  type CrankingCircuit,
} from "@/lib/cranking-circuit";
import {
  computeCableDemands,
  type CableDemand,
} from "@/lib/diagnostics/sizing";
import { makeSystemCableSpecGetter } from "@/lib/cable-spec";
import { computeCableDemandAnalysis } from "@/lib/cable-demands";
import { withoutDanglingCables } from "@/lib/diagnostics/cable-endpoints";
import { computeLiveCableCurrents } from "@/lib/live-cable-flow";
import { indexNodesById } from "@/lib/node-tree";
import { isSenseCable } from "@/lib/sense-terminal";
import { useSystemStore } from "@/lib/store";
import { formatLiveCurrent } from "@/lib/utils";
import { computePathMinWireSizes } from "@/lib/path-vdrop-budget";
import type { Cable, System } from "@/lib/types";
import {
  computeVdropAllowances,
  type VdropAllowances,
} from "@/lib/vdrop-allowance";
import { busNominalVoltage, resolveVoltageDomains } from "@/lib/voltage-domain";

/**
 * Memoized wrappers around the heavy graph walks. The `simulate` context must
 * already be memoized by the caller — a fresh object each render defeats every
 * cache in here.
 */

export function useCableDemands(
  system: System,
  simulate?: SimulateContext,
): Map<string, CableDemand> {
  // A drag hands us a new `system` ref every frame but can't change demand, so
  // reuse the last answer rather than re-walking 60× a second.
  const isDragging = useCanvasDrag((s) => s.isDragging);
  const lastRef = useRef<Map<string, CableDemand> | null>(null);
  return useMemo(() => {
    if (isDragging && lastRef.current) return lastRef.current;
    const result = computeCableDemands(system, getDefinition, simulate);
    lastRef.current = result;
    return result;
  }, [system, simulate, isDragging]);
}

/** Simulate mode nets the bus's sources against its loads; edit mode returns
 *  the worst-case sizing current. */
export function useBatteryBankCurrents(
  system: System,
  simulate?: SimulateContext,
): BatteryBankCurrents {
  const isDragging = useCanvasDrag((s) => s.isDragging);
  const lastRef = useRef<BatteryBankCurrents | null>(null);
  return useMemo(() => {
    if (isDragging && lastRef.current) return lastRef.current;
    const result = computeBatteryBankCurrents(system, getDefinition, simulate);
    lastRef.current = result;
    return result;
  }, [system, simulate, isDragging]);
}

/** The inspector keeps design sizing separate from what is flowing now. */
export function useSimulatedCableCurrent(system: System, cable: Cable | undefined): string | undefined {
  const mode = useSystemStore(s => s.mode);
  const componentStates = useSystemStore(s => s.simulateState.componentStates);
  const loadStates = useSystemStore(s => s.simulateState.loadStates);
  return useMemo(() => {
    if (mode !== "simulate" || !cable) return undefined;
    const nodes = indexNodesById(system.nodes);
    const signed = computeLiveCableCurrents(nodes, system.cables, getDefinition, { componentStates, loadStates }).get(cable.id);
    if (signed == null) return undefined;
    const sense = isSenseCable(cable, nodes, getDefinition);
    // A known subtotal on a shared return is not its total. Dedicated
    // monitor feeds are independent of unknown loads elsewhere on the bus.
    if (!sense && computeCableDemandAnalysis(withoutDanglingCables(system, getDefinition), getDefinition)
      .operatingFallbackCableIds.has(cable.id)) return undefined;
    return formatLiveCurrent(Math.abs(signed), sense);
  }, [system, cable, mode, componentStates, loadStates]);
}

/** Not simulate-aware: ABYC E-11.10 exempts the cranking *conductor*, whatever
 *  the key is doing. */
export function useCrankingCables(system: System): CrankingCircuit {
  const isDragging = useCanvasDrag((s) => s.isDragging);
  const lastRef = useRef<CrankingCircuit | null>(null);
  return useMemo(() => {
    if (isDragging && lastRef.current) return lastRef.current;
    const result = computeCrankingCables(system, getDefinition);
    lastRef.current = result;
    return result;
  }, [system, isDragging]);
}

const EMPTY_PATH_MINS: ReadonlyMap<string, number> = new Map();

/** Use inside a component instead of `computeSystemCableSpec`, which rebuilds
 *  every map on every call. */
export function useCableSpecGetter(
  system: System,
  autoSizeTargetPct: number,
  simulate?: SimulateContext,
): (cable: Cable) => CableSpec {
  const isDragging = useCanvasDrag((s) => s.isDragging);
  const lastRef = useRef<((cable: Cable) => CableSpec) | null>(null);
  return useMemo(() => {
    if (isDragging && lastRef.current) return lastRef.current;
    const result = makeSystemCableSpecGetter(system, getDefinition, simulate, autoSizeTargetPct);
    lastRef.current = result;
    return result;
  }, [system, simulate, autoSizeTargetPct, isDragging]);
}

/** Per-circuit ABYC E-11.14.2.6 V-drop allowances. Not simulate-aware: the
 *  allowance is a design-time property of the circuit. */
export function useVdropAllowances(
  system: System,
  autoSizeTargetPct: number,
): VdropAllowances {
  const isDragging = useCanvasDrag((s) => s.isDragging);
  const lastRef = useRef<VdropAllowances | null>(null);
  return useMemo(() => {
    if (isDragging && lastRef.current) return lastRef.current;
    const result = computeVdropAllowances(
      system,
      getDefinition,
      autoSizeTargetPct,
    );
    lastRef.current = result;
    return result;
  }, [system, autoSizeTargetPct, isDragging]);
}

/** Not simulate-aware: a domain's nominal voltage is set by what is wired into
 *  it, not by which switches are closed. */
export function useBusNominalVoltage(system: System): number {
  const isDragging = useCanvasDrag((s) => s.isDragging);
  const lastRef = useRef<number | null>(null);
  return useMemo(() => {
    if (isDragging && lastRef.current != null) return lastRef.current;
    const result = busNominalVoltage(resolveVoltageDomains(system, getDefinition));
    lastRef.current = result;
    return result;
  }, [system, isDragging]);
}

export function usePathMinWireSizes(
  system: System,
  autoSizeTargetPct: number,
  // Simulate callers pass `enabled: false` and still call the hook, so React
  // sees a stable hook order across a mode change.
  options: { enabled?: boolean } = {},
  simulate?: SimulateContext,
): ReadonlyMap<string, number> {
  const enabled = options.enabled ?? true;
  const isDragging = useCanvasDrag((s) => s.isDragging);
  const lastRef = useRef<ReadonlyMap<string, number> | null>(null);
  return useMemo(() => {
    if (!enabled) return EMPTY_PATH_MINS;
    if (isDragging && lastRef.current) return lastRef.current;
    const result = computePathMinWireSizes(
      system,
      getDefinition,
      simulate,
      autoSizeTargetPct,
    );
    lastRef.current = result;
    return result;
  }, [enabled, system, autoSizeTargetPct, simulate, isDragging]);
}
